import React, { useState } from 'react'
import { toast } from 'react-toastify'

import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Typography
} from '@mui/material'
import copy from 'copy-to-clipboard'

import { convertToCustomPortalUrl } from 'utils/domain-urls'

import { theme } from 'styles/theme'

interface CopyLinkCoupon {
  uuid: string
  code: string
}

interface CopyLinkPopoverProps {
  linkUrl: string
  coupons: CopyLinkCoupon[]
  children: (props: {
    onClick: (e: React.MouseEvent<HTMLElement>) => void
  }) => React.ReactNode
}

const buildCopyUrl = (baseUrl: string, couponCode?: string): string => {
  const url = convertToCustomPortalUrl(baseUrl)

  if (!couponCode) return url

  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}cupom=${couponCode}`
}

const CopyLinkPopover: React.FC<CopyLinkPopoverProps> = ({
  linkUrl,
  coupons,
  children
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()

    if (coupons.length === 0) {
      copy(convertToCustomPortalUrl(linkUrl))
      toast.success('Link copiado com sucesso!')
      return
    }

    setAnchorEl(e.currentTarget)
  }

  const handleCopy = (couponCode?: string) => {
    const url = buildCopyUrl(linkUrl, couponCode)
    copy(url)
    toast.success('Link copiado com sucesso!')
    setAnchorEl(null)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <>
      {children({ onClick: handleClick })}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '8px',
              minWidth: 220,
              boxShadow: '0px 4px 16px rgba(0,0,0,0.12)',
              mt: 0.5
            }
          }
        }}
      >
        <Box px={1} pt={1.5} pb={0.5}>
          <Typography
            variant="smBold"
            color={theme.palette.neutral[300]}
            px={1}
            mb={0.5}
          >
            Copiar:
          </Typography>

          <List dense disablePadding sx={{ maxHeight: 250, overflowY: 'auto' }}>
            <ListItemButton
              onClick={() => handleCopy()}
              sx={{ borderRadius: '6px', py: 0.75 }}
            >
              <ListItemText
                primary="Sem cupom"
                primaryTypographyProps={{
                  fontSize: 14,
                  color: theme.palette.neutral[200]
                }}
              />
            </ListItemButton>

            {coupons.map((coupon) => (
              <ListItemButton
                key={coupon.uuid}
                onClick={() => handleCopy(coupon.code)}
                sx={{ borderRadius: '6px', py: 0.75 }}
              >
                <ListItemText
                  primary={`Com cupom ${coupon.code}`}
                  primaryTypographyProps={{
                    fontSize: 14,
                    color: theme.palette.neutral[200]
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Popover>
    </>
  )
}

export default CopyLinkPopover
